/* eslint-disable max-len */
import { AddendumStatus } from '@prisma/client';
import { Status } from '../types/Status';
import dayjs from 'dayjs';

export const mapStatus = (
  needed: boolean,
  completed: boolean,
  pending: boolean
) => {
  if (needed) {
    return 'needed';
  }
  if (completed) {
    return 'completed';
  }
  if (pending) {
    return 'pending';
  }
  return 'on hold';
};

export const STATUS_TYPES = ['needed', 'completed', 'pending', 'on hold'];

export const TRANSACTION_STEPS = [
  'showing',
  'offer',
  'inspection',
  'appraisal',
  'title',
  'financing',
  'finalWalkthrough',
  'closing',
] as const;

export const STEP_LABELS: Record<(typeof TRANSACTION_STEPS)[number], string> = {
  showing: 'Showings',
  offer: 'Offers',
  inspection: 'Inspections',
  appraisal: 'Appraisal',
  title: 'Title',
  financing: 'Financing',
  finalWalkthrough: 'Final Walkthrough',
  closing: 'Closing',
};

export const getStepStatusAndInfo = (
  step: (typeof TRANSACTION_STEPS)[number],
  needed: boolean,
  completed: boolean,
  pending: boolean,
  addendum: AddendumStatus | null,
  dueDate?: Date,
  inspectionDate?: Date,
  isBuyer = false,
  isPBuyer = false,
  inspectionNotApplicable = false
): { status: string; statusText: string } => {
  let status: (typeof STATUS_TYPES)[number];

  if (needed) {
    status = 'needed';
  } else if (completed) {
    status = 'completed';
  } else if (pending) {
    status = 'pending';
  } else {
    status = 'on hold';
  }

  if (step === 'inspection' && addendum !== null) {
    status = 'repairs';
  }

  const isSeller = !isBuyer && !isPBuyer;

  const educationalInfo = isPBuyer
    ? BUYER_OF_HOMEPOINT_LISTING_EDUCATIONAL_INFO
    : isBuyer
    ? BUYER_STEP_EDUCATIONAL_INFO
    : STEP_EDUCATIONAL_INFO;

  let statusText =
    educationalInfo[step][
      status as keyof (typeof educationalInfo)[typeof step]
    ] ?? '';

  if (step === 'inspection' && dueDate && isSeller && status === 'needed') {
    const preText = 'The buyer needs to schedule an inspection by';
    statusText = `${preText} ${dayjs.utc(dueDate).format('MM/DD/YYYY')}`;
  }

  if (
    step === 'inspection' &&
    inspectionDate &&
    dueDate &&
    isSeller &&
    status === 'pending'
  ) {
    const preText = 'The inspection is scheduled for';
    statusText = `${preText} ${dayjs
      .utc(inspectionDate)
      .format(
        'MM/DD/YYYY'
      )}. The buyer may have requests for repairs from the inspection. The buyer has until ${dayjs
      .utc(dueDate)
      .format('MM/DD/YYYY')} to finish inspections and request repairs.`;
  }

  return {
    statusText,
    status,
  };
};

export const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
      return '#4CAF50'; // Green
    case 'pending':
      return '#2196F3'; // Blue
    case 'on hold':
      return '#9E9E9E'; // Gray
    case 'needed':
      return '#FF9800'; // Orange
    case 'repairs':
      return '#FF9800'; // Red
    default:
      return '#9E9E9E';
  }
};

export const mapStatusToProgressBar = (
  step: (typeof TRANSACTION_STEPS)[number],
  status: Status,
  isBuyer = false,
  isPBuyer = false
) => {
  switch (step) {
    case 'showing':
      return getStepStatusAndInfo(
        step,
        false,
        status.showing.completed,
        status.showing.data.length > 0,
        null,
        undefined,
        undefined,
        isBuyer,
        isPBuyer
      );
    case 'offer':
      return getStepStatusAndInfo(
        step,
        false,
        status.offer.completed,
        status.offer.data.length > 0,
        null,
        undefined,
        undefined,
        isBuyer,
        isPBuyer
      );
    case 'inspection':
      return getStepStatusAndInfo(
        step,
        status.inspection.needed,
        status.inspection.completed,
        status.inspection.pending,
        status.inspection.addendum,
        status.inspection.dueDate,
        status.inspection.inspectionDate,
        isBuyer,
        isPBuyer
      );
    case 'appraisal':
      return getStepStatusAndInfo(
        step,
        status.appraisal.needed,
        status.appraisal.completed,
        status.appraisal.pending,
        null,
        undefined,
        undefined,
        isBuyer,
        isPBuyer
      );
    case 'financing':
      return getStepStatusAndInfo(
        step,
        status.financing.needed,
        status.financing.completed,
        status.financing.pending,
        null,
        undefined,
        undefined,
        isBuyer,
        isPBuyer
      );
    case 'title':
      return getStepStatusAndInfo(
        step,
        status.title.needed,
        status.title.completed,
        status.title.pending,
        null,
        undefined,
        undefined,
        isBuyer,
        isPBuyer
      );
    case 'finalWalkthrough':
      return getStepStatusAndInfo(
        step,
        status.finalWalkthrough.needed,
        status.finalWalkthrough.completed,
        status.finalWalkthrough.pending,
        null,
        status.finalWalkthrough.scheduledDate,
        undefined,
        isBuyer,
        isPBuyer
      );
    case 'closing':
      return getStepStatusAndInfo(
        step,
        status.closing.needed,
        status.closing.completed,
        status.closing.pending,
        null,
        undefined,
        undefined,
        isBuyer,
        isPBuyer
      );
  }
};

type StepEducationalInfo = {
  [K in keyof typeof STEP_LABELS]: {
    needed: string;
    completed: string;
    pending: string;
    'on hold': string;
    repairs?: string;
  };
};

export const STEP_EDUCATIONAL_INFO: StepEducationalInfo = {
  showing: {
    needed:
      'No showings have been scheduled yet. Keep your home ready for viewing requests.',
    completed: 'You have an accepted offer and showings are complete.',
    pending:
      'Showings are still happening. Keep your home ready for when buyers express interest.',
    'on hold':
      'No current showing requests. Keep your home ready for when buyers express interest.',
  },
  offer: {
    needed:
      "No offers have been submitted yet. Continue maintaining your home's showing condition.",
    completed:
      "You've accepted an offer! We'll now proceed with the buyer's due diligence steps.",
    pending:
      'You have received an offer. Review it with your agent to make your decision.',
    'on hold':
      'No active offers at this time. Consider feedback from previous showings if available.',
  },
  inspection: {
    needed:
      'The buyer needs to schedule their home inspection. They typically have 7-10 days to complete this.',
    completed: 'Inspections are complete. Congratulations!',
    pending:
      'Inspections are in progress. Please ensure all utilities and equipment are on. After inspections, the buyer may request repairs.',
    repairs:
      'The buyer has submitted repair requests. This is a list of repairs that need to be made to the property before closing.',
    'on hold':
      "Inspection period hasn't started yet. This will begin once you accept an offer.",
  },
  appraisal: {
    needed:
      "The buyer's lender requires an appraisal. They will schedule this directly.",
    completed:
      "Appraisal is complete. The results have been sent to the buyer's lender.",
    pending:
      'Appraisal is scheduled. Please ensure the property is accessible at the arranged time.',
    'on hold':
      "Appraisal hasn't been scheduled yet. This typically occurs after the inspection period.",
  },
  title: {
    needed:
      'Title company information is being confirmed.',
    completed:
      'Title company details are complete. We can proceed with the next steps.',
    pending:
      'Title company is conducting their search. They may contact you if they need additional information.',
    'on hold':
      "Title work hasn't begun yet. This typically occurs after the inspection period.",
  },
  financing: {
    needed:
      'Buyer needs to complete their mortgage approval process. This may take 2-3 weeks.',
    completed:
      "Buyer's financing is approved! We're clear to move toward closing.",
    pending:
      'Buyer is working with their lender to finalize financing. The lender may request an appraisal.',
    'on hold':
      "Buyer's financing process hasn't started yet. This begins after offer acceptance.",
  },
  finalWalkthrough: {
    needed:
      'The buyer needs to schedule their final walkthrough. This typically happens 24-48 hours before closing.',
    completed:
      'Final walkthrough is complete. The buyer has verified the property condition.',
    pending:
      'Final walkthrough is scheduled. The buyer will verify the property is in the agreed-upon condition.',
    'on hold':
      "Final walkthrough hasn't been scheduled yet. This happens after inspections are complete.",
  },
  closing: {
    needed:
      'Time to schedule the closing. Your agent will coordinate with all parties to set a date.',
    completed:
      'Congratulations! The sale has closed and funds have been transferred.',
    pending:
      'Closing is scheduled. Prepare for the final walkthrough and document signing.',
    'on hold':
      "We're not ready for closing yet. Other steps need to be completed first.",
  },
};

// Create a new educational info object for buyers
export const BUYER_STEP_EDUCATIONAL_INFO: StepEducationalInfo = {
  showing: {
    needed:
      'No showings have been scheduled yet. Work with your agent to find properties that match your criteria.',
    completed: 'You have an accepted offer and showings are complete.',
    pending:
      'Showings are still happening. Continue viewing properties that interest you.',
    'on hold':
      'No current showings scheduled. Work with your agent to find properties that match your criteria.',
  },
  offer: {
    needed:
      "You haven't submitted an offer yet. When you find a property you like, your agent will help you prepare one.",
    completed:
      "Your offer has been accepted! We'll now proceed with your due diligence steps.",
    pending:
      'Your offer has been submitted. The seller is reviewing it and may accept, reject, or counter.',
    'on hold':
      'No active offers at this time. Continue looking for properties that meet your needs.',
  },
  inspection: {
    needed:
      'You need to schedule a home inspection. You typically have 7-10 days to complete this.',
    completed: 'Inspections are complete.',
    pending:
      'Inspections are in progress. After inspections, you may request repairs based on the findings.',
    repairs:
      'You have submitted repair requests. The seller is reviewing your repair addendum.',
    'on hold':
      "Inspection period hasn't started yet. This will begin once your offer is accepted.",
  },
  appraisal: {
    needed:
      'Your lender requires an appraisal. They will schedule this directly with the seller.',
    completed:
      'Appraisal is complete. The results have been sent to your lender for review.',
    pending:
      'Appraisal is scheduled. Your lender will use this to verify the property value.',
    'on hold':
      "Appraisal hasn't been scheduled yet. This typically occurs after the inspection period.",
  },
  title: {
    needed:
      'Title company details are needed. Your Homepoint agent will provide this information.',
    completed:
      'Title search is complete and clear. We can proceed with the next steps.',
    pending:
      'Title company is conducting their search. They will ensure there are no liens or issues with the property.',
    'on hold':
      "Title work hasn't begun yet. This typically occurs after the inspection period.",
  },
  financing: {
    needed:
      "You need to submit your lender's information so we may coordinate with them.",
    completed:
      "Your financing is approved! We're clear to move toward closing.",
    pending:
      'You are working with your lender to finalize financing. They may request additional documentation.',
    'on hold':
      "Your financing process hasn't started yet. This begins after offer acceptance.",
  },
  finalWalkthrough: {
    needed:
      'You need to schedule your final walkthrough. This is your last chance to verify the property condition before closing.',
    completed:
      'Your final walkthrough is complete. You have verified the property is in the expected condition.',
    pending:
      'Your final walkthrough is scheduled. This is when you verify the property is in the agreed-upon condition and any repairs have been completed.',
    'on hold':
      "Your final walkthrough hasn't been scheduled yet. This happens after inspections are complete.",
  },
  closing: {
    needed:
      'Time to schedule the closing. Your agent will coordinate with all parties to set a date.',
    completed:
      'Congratulations! The purchase has closed and you are now a homeowner!',
    pending:
      'Closing is scheduled. Prepare for the final walkthrough and document signing.',
    'on hold':
      "We're not ready for closing yet. Other steps need to be completed first.",
  },
};

// Create educational info for buyers purchasing a Homepoint-listed property
export const BUYER_OF_HOMEPOINT_LISTING_EDUCATIONAL_INFO: StepEducationalInfo =
  {
    showing: {
      needed:
        'No showings have been scheduled yet. Contact your agent to schedule a viewing of this property.',
      completed:
        'The seller has accepted your offer and showings are complete.',
      pending:
        'Showings are happening for this property. Schedule yours soon as this may indicate strong interest.',
      'on hold':
        'No current showing activity. This is a good time to schedule a viewing with your agent.',
    },
    offer: {
      needed:
        'No offers have been submitted on this property yet. This could be a great opportunity for you to make a competitive offer.',
      completed:
        'The seller has accepted your offer. This property is now under contract to you.',
      pending:
        'The seller is reviewing your offer. They may accept, reject, or counter your proposal.',
      'on hold':
        'No active offers at this time. Consider making an offer if this property meets your needs.',
    },
    inspection: {
      needed:
        'You need to schedule your home inspection. You typically have 7-10 days to complete this.',
      completed: 'Your inspections are complete.',
      pending:
        'You are conducting your inspections. You may request repairs based on the findings.',
      repairs:
        'You have requested repairs from the seller. They are reviewing your repair addendum.',
      'on hold': "Your inspection period hasn't started yet.",
    },
    appraisal: {
      needed: 'Your lender requires an appraisal of this property.',
      completed:
        'Your appraisal is complete. The results have been sent to your lender.',
      pending: 'Your appraisal is being conducted for your financing approval.',
      'on hold': "Your appraisal hasn't been scheduled yet.",
    },
    title: {
      needed: 'Title company information is being confirmed for your transaction.',
      completed:
        'Title work is complete and the property has a clear title for your purchase.',
      pending:
        'The title company is conducting their search for your transaction.',
      'on hold': "Title work hasn't begun yet for your purchase.",
    },
    financing: {
      needed:
        'You need to complete your mortgage approval process for this property.',
      completed: 'Your financing is approved for this property purchase.',
      pending:
        'You are working with your lender to finalize financing for this property.',
      'on hold': "Your financing process hasn't started yet.",
    },
    finalWalkthrough: {
      needed: 'You need to schedule your final walkthrough of this property.',
      completed: 'Your final walkthrough is complete.',
      pending: 'Your final walkthrough is scheduled for this property.',
      'on hold': "Your final walkthrough hasn't been scheduled yet.",
    },
    closing: {
      needed: 'Your closing is being scheduled for this property purchase.',
      completed:
        'Congratulations! You have successfully purchased this property.',
      pending: 'Your closing is scheduled. Prepare for document signing.',
      'on hold':
        "You're not ready for closing yet. Other steps need to be completed first.",
    },
  };
